/**
 * AuthErrorBoundary - Error boundary specifically for authentication flows
 * Provides graceful error handling and recovery options for auth-related errors
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';

// ============================================================================
// Types
// ============================================================================

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error | null;
  errorInfo?: ErrorInfo | null;
}

// ============================================================================
// Styled Components
// ============================================================================

const ErrorContainer = styled.div`
  text-align: center;
  padding: 2rem;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-light);
`;

const ErrorIcon = styled.div`
  font-size: 1.75rem;
  margin-bottom: 1rem;
  opacity: 0.6;
`;

const ErrorTitle = styled.h2`
  margin: 0 0 0.5rem 0;
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
`;

const ErrorMessage = styled.p`
  margin: 0 0 2rem 0;
  color: var(--text-muted);
  font-size: 1rem;
  line-height: 1.5;
`;

const ErrorActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
`;

const ErrorDetails = styled.details`
  margin-top: 1.5rem;
  padding: 1rem;
  background: var(--neutral-25);
  border-radius: var(--radius-sm);
  border: 1px solid var(--border);
  text-align: left;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;

  summary {
    cursor: pointer;
    font-weight: 600;
    color: var(--text-muted);
    font-size: 0.9rem;
    margin-bottom: 0.5rem;

    &:hover {
      color: var(--text-primary);
    }
  }

  pre {
    font-size: 0.8rem;
    color: var(--text-muted);
    white-space: pre-wrap;
    word-break: break-word;
    margin: 0.5rem 0 0 0;
    background: var(--surface);
    padding: 0.75rem;
    border-radius: var(--radius-sm);
    border: 1px solid var(--border);
    overflow-x: auto;
  }
`;

// ============================================================================
// Error Boundary Component
// ============================================================================

export class AuthErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Auth Error Boundary caught an error:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/auth/login';
  };

  override render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <ErrorContainer>
          <ErrorIcon>
            <Icon name="warning" size="xl" aria-hidden />
          </ErrorIcon>
          <ErrorTitle>Algo salió mal</ErrorTitle>
          <ErrorMessage>
            Ha ocurrido un error inesperado en el proceso de autenticación.
            Puedes intentar recargar la página o volver al inicio de sesión.
          </ErrorMessage>

          <ErrorActions>
            <Button variant="primary" size="md" onClick={this.handleReset}>
              Intentar de nuevo
            </Button>

            <Button variant="secondary" size="md" onClick={this.handleReload}>
              Recargar página
            </Button>

            <Button variant="tertiary" size="sm" onClick={this.handleGoHome}>
              Volver al login
            </Button>
          </ErrorActions>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <ErrorDetails>
              <summary>Detalles del error (solo en desarrollo)</summary>
              <pre>
                {this.state.error.name}: {this.state.error.message}
                {'\n\n'}
                {this.state.error.stack}
                {this.state.errorInfo?.componentStack &&
                  `\n\nComponent Stack:${this.state.errorInfo.componentStack}`}
              </pre>
            </ErrorDetails>
          )}
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default AuthErrorBoundary;
